import { useCallback, useEffect, useRef, useState } from "react";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import { ClientHelper } from "../client/CreateNswagClient";
import { LiveShareSendRequest, SignalRConnectionInfo } from "../client/SmartAssistantBackendClient";
import { ChatMessageModel } from "../client/SmartAssistantBackendClient";
import { useCurrentThreadContext } from "../contexts/CurrentThreadContext";
import { useLoginContext } from "../components/login/LoginHandler";

interface LiveShareState {
    open: boolean;
    id: string | null;
    link: string;
    active: boolean;
    connecting: boolean;
    sharedLoading: boolean;
}

interface UseLiveShareResult extends LiveShareState {
    openDialog: () => Promise<void>;
    closeDialog: () => void;
    stop: () => Promise<void>;
    broadcast: (messages: ChatMessageModel[], chatId?: string | null) => void;
}

export const useLiveShare = (): UseLiveShareResult => {
    const {
        threadId: chatId,
        history,
        replaceHistory,
        setCurrentThreadSelection,
        agentSelection,
        setAgentSelection,
    } = useCurrentThreadContext();
    const consumer = useLoginContext();
    const createChatId = useCallback(() => {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        return `chat-${Math.random().toString(36).slice(2, 10)}`;
    }, []);

    const [open, setOpen] = useState(false);
    const [id, setId] = useState<string | null>(null);
    const [link, setLink] = useState<string>("");
    const [active, setActive] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [sharedLoading, setSharedLoadingState] = useState(false);

    const connectionRef = useRef<HubConnection | null>(null);
    const clientIdRef = useRef<string>(
        typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `client-${Math.random().toString(36).slice(2, 10)}`
    );
    const localChatIdRef = useRef<string | null>(null);
    const lastLocalUpdateAtRef = useRef<number>(0);
    const isInitiatorRef = useRef(false);
    const readyRef = useRef(false);
    const syncAttemptsRef = useRef(0);
    const suppressRef = useRef(false);
    const historyRef = useRef<ChatMessageModel[]>(history);

    useEffect(() => {
        historyRef.current = history;
    }, [history]);

    const buildPayload = useCallback((messages?: ChatMessageModel[], forceFull?: boolean) => {
        const updatedAt = Date.now();
        lastLocalUpdateAtRef.current = updatedAt;
        const localChatId = localChatIdRef.current ?? (chatId ?? createChatId());
        localChatIdRef.current = localChatId;
        const full = forceFull || !messages;
        if (full) {
            return {
                type: "full",
                chatId: localChatId,
                messages: historyRef.current,
                agentSelection,
                sharedLoading,
                updatedAt,
            };
        }
        return {
            type: "delta",
            chatId: localChatId,
            messages: messages ?? [],
            agentSelection,
            sharedLoading,
            updatedAt,
        };
    }, [agentSelection, chatId, createChatId, sharedLoading]);

    const applyPayload = useCallback((payload: any) => {
        if (!payload || !Array.isArray(payload.messages)) return;
        readyRef.current = true;
        syncAttemptsRef.current = 0;
        const normalizedMessages = payload.messages.map((m: any) => ChatMessageModel.fromJS(m));
        suppressRef.current = true;
        const localChatId = payload.chatId ?? localChatIdRef.current ?? (chatId ?? createChatId());
        localChatIdRef.current = localChatId;
        const existing = historyRef.current ?? [];
        if (payload.agentSelection) {
            setAgentSelection(payload.agentSelection);
        }
        if (payload.type === "full") {
            setCurrentThreadSelection(localChatId, normalizedMessages);
        } else {
            if (normalizedMessages.length === 0) return;
            const merged = [...existing, ...normalizedMessages];
            if (localChatId !== chatId) {
                setCurrentThreadSelection(localChatId, merged);
            } else {
                replaceHistory(merged, localChatId);
            }
        }
        if (typeof payload.sharedLoading === "boolean") {
            setSharedLoadingState(payload.sharedLoading);
        }
        setTimeout(() => {
            suppressRef.current = false;
        }, 0);
    }, [chatId, createChatId, replaceHistory, setAgentSelection, setCurrentThreadSelection]);

    const sendPayload = useCallback(async (shareId: string, payload: any) => {
        const token = await consumer.getUserToken();
        const liveShareClient = ClientHelper.createLiveShareClient(token);
        const withSenderMeta = (value: any) => {
            if (!value || typeof value !== "object") return value;
            return {
                ...value,
                meta: {
                    ...(value.meta ?? {}),
                    senderId: clientIdRef.current,
                },
            };
        };
        const payloadToSend = withSenderMeta(payload);
        try {
            const payloadSize = JSON.stringify(payloadToSend ?? {}).length;
            const updatedAt = payloadToSend?.updatedAt;
            console.log("[LiveShare][Send]", { shareId, senderId: clientIdRef.current, updatedAt, payloadSize });
        } catch {
            console.log("[LiveShare][Send]", { shareId, senderId: clientIdRef.current });
        }
        await liveShareClient.send(new LiveShareSendRequest({
            shareId,
            senderId: clientIdRef.current,
            payload: payloadToSend,
        }));
    }, [consumer]);

    const broadcast = useCallback((messages: ChatMessageModel[], nextChatId?: string | null) => {
        if (!active || !id) return;
        if (!messages?.length) return;
        if (nextChatId) {
            localChatIdRef.current = nextChatId;
        }
        const payload = buildPayload(messages);
        sendPayload(id, payload).catch(() => undefined);
    }, [active, buildPayload, id, sendPayload]);

    const connect = useCallback(async (shareId: string, isInitiator: boolean) => {
        if (!shareId) return;
        if (connectionRef.current?.state === HubConnectionState.Connected) return;
        setConnecting(true);
        const isOwner = typeof sessionStorage !== "undefined"
            && sessionStorage.getItem(`liveShareOwner:${shareId}`) === "true";
        const initiatorMode = isInitiator || isOwner;
        isInitiatorRef.current = initiatorMode;
        readyRef.current = initiatorMode;
        try {
            const token = await consumer.getUserToken();
            const signalRClient = ClientHelper.createSignalRClient(token);
            const connectionInfo: SignalRConnectionInfo = await signalRClient.negotiate(shareId);

            const connection = new HubConnectionBuilder()
                .withUrl(connectionInfo.url ?? "", {
                    accessTokenFactory: () => connectionInfo.accessToken ?? "",
                })
                .withAutomaticReconnect([0, 2000, 10000, 30000, 300000])
                .configureLogging(LogLevel.Information)
                .build();

            connection.on("liveShare", (msg: any) => {
                console.log("[LiveShare][Recv raw]", msg);
                const parsed = typeof msg === "string" ? JSON.parse(msg) : msg;
                if (parsed?.senderId && parsed.senderId === clientIdRef.current) return;
                let payload = parsed?.payload ?? parsed;
                if (typeof payload === "string") {
                    try {
                        payload = JSON.parse(payload);
                    } catch {
                        // keep as string
                    }
                }
                if (payload?.meta?.senderId && payload.meta.senderId === clientIdRef.current) return;
                try {
                    const payloadSize = JSON.stringify(payload ?? {}).length;
                    const updatedAt = payload?.updatedAt;
                    console.log("[LiveShare][Recv]", { shareId, senderId: parsed?.senderId, updatedAt, payloadSize });
                } catch {
                    console.log("[LiveShare][Recv]", { shareId, senderId: parsed?.senderId });
                }
                if ((payload?.meta?.requestSync || payload?.meta?.messageType === "joined") && isInitiatorRef.current) {
                    sendPayload(shareId, buildPayload(undefined, true));
                    return;
                }
                applyPayload(payload);
            });

            await connection.start();
            connectionRef.current = connection;
            if (!initiatorMode) {
                const sendJoin = () => {
                    if (readyRef.current) return;
                    sendPayload(shareId, { meta: { messageType: "joined" } }).catch(() => undefined);
                };
                sendJoin();
                syncAttemptsRef.current = 1;
                const retrySync = () => {
                    if (readyRef.current) return;
                    if (syncAttemptsRef.current >= 3) return;
                    syncAttemptsRef.current += 1;
                    sendJoin();
                    setTimeout(retrySync, 1500);
                };
                setTimeout(retrySync, 1500);
            }
        } catch (err) {
            console.error("Live share connect failed", err);
        } finally {
            setConnecting(false);
        }
    }, [applyPayload, buildPayload, sendPayload, consumer]);

    const start = useCallback(async () => {
        const token = await consumer.getUserToken();
        console.log("[LiveShare][Start] initiating");
        const liveShareClient = ClientHelper.createLiveShareClient(token);
        const data = await liveShareClient.start();
        const shareId = data?.shareId;
        if (!shareId) return;
        console.log("[LiveShare][Start] shareId", shareId);
        if (typeof sessionStorage !== "undefined") {
            sessionStorage.setItem(`liveShareOwner:${shareId}`, "true");
        }
        const shareLink = `${window.location.origin}${window.location.pathname}?share=${shareId}`;
        setId(shareId);
        setLink(shareLink);
        setActive(true);
        isInitiatorRef.current = true;
        readyRef.current = true;
        localChatIdRef.current = chatId ?? createChatId();
        if (!chatId) {
            replaceHistory(historyRef.current, localChatIdRef.current);
        }
        await connect(shareId, true);
    }, [chatId, connect, createChatId, consumer, replaceHistory]);

    const openDialog = useCallback(async () => {
        if (!id) {
            await start();
        }
        setOpen(true);
    }, [id, start]);

    const stop = useCallback(async () => {
        try {
            await connectionRef.current?.stop();
        } catch {
            // ignore
        }
        connectionRef.current = null;
        setActive(false);
        setId(null);
        setLink("");
        isInitiatorRef.current = false;
        readyRef.current = false;
        if (typeof sessionStorage !== "undefined" && id) {
            sessionStorage.removeItem(`liveShareOwner:${id}`);
        }
    }, [id]);

    useEffect(() => {
        const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
        const share = params?.get("share");
        if (!share || active) return;
        setId(share);
        setLink(`${window.location.origin}${window.location.pathname}?share=${share}`);
        setActive(true);
        localChatIdRef.current = chatId ?? createChatId();
        if (!chatId) {
            replaceHistory(historyRef.current, localChatIdRef.current);
        }
        connect(share, false).catch(() => undefined);
    }, [active, chatId, connect, createChatId, replaceHistory]);

    return {
        open,
        id,
        link,
        active,
        connecting,
        sharedLoading,
        openDialog,
        closeDialog: () => setOpen(false),
        stop,
        broadcast,
    };
};
